// =============================================================================
// sharedAssumptions.js — User-scoped, cross-page assumption defaults
// =============================================================================
//
// Why this exists (Phase 70, trust-audit fix A2):
//   Projections and FI each have their own "assumptions" payload that is
//   persisted to the backend independently. The two pages share two
//   conceptually identical knobs — `nominal_return_pct` and
//   `inflation_pct` — but historically each page only knew about its
//   own copy. The result: a user who set "10% return" on FI would then
//   open Projections and see "8% return" again, because Projections
//   reads only its own server-side assumptions.
//
//   That feels like the app doesn't remember what the user said, which
//   erodes trust in a finance app more than any single feature would
//   add. A user who has to re-tell us their assumptions everywhere
//   reasonably concludes that the rest of the data probably doesn't
//   sync either.
//
// Strategy (deliberately small):
//   We keep the existing per-page server-side persistence intact. On top
//   of it, we mirror the *shared* fields (return %, inflation %) to a
//   user-scoped localStorage key whenever a page commits an Apply or
//   debounced auto-recompute. The other page reads from this mirror on
//   first load as a *fallback* — only used when the server hasn't given
//   that page an explicit per-page value.
//
// Storage shape:
//   localStorage['2120_sa_<userId>'] = {
//     nominal_return_pct: 9,
//     inflation_pct: 6,
//     updated_at: 1719337200000,
//   }
//
// Safety:
//   • Keys are scoped by userId, so two users on the same device never
//     pollute each other's defaults.
//   • Best-effort: every call wraps localStorage access in try/catch so
//     private-browsing or quota-exceeded states don't crash the page.
//   • Stale guard: entries older than 90 days are ignored on read. A
//     user who hasn't touched FI/Projections in three months should get
//     fresh server-side defaults instead of a long-forgotten override.
// =============================================================================

const KEY_PREFIX = '2120_sa_';
const STALE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

// The fields we consider truly shared. Page-specific knobs
// (`withdrawal_rate_pct`, `extra_emi_monthly`, etc.) stay scoped to
// their respective page — they don't generalise.
const SHARED_FIELDS = ['nominal_return_pct', 'inflation_pct'];

function _key(userId) {
  return `${KEY_PREFIX}${userId || 'anon'}`;
}

function _safeParse(raw) {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (!v || typeof v !== 'object') return null;
    return v;
  } catch {
    return null;
  }
}

/**
 * Read the user's shared assumption defaults, if any. Returns `null`
 * when nothing is stored, the entry is too old, or storage is
 * unavailable. Callers should treat a `null` return as "no override
 * exists; fall back to your built-in defaults".
 */
export function readSharedAssumptions(userId) {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(_key(userId));
    const parsed = _safeParse(raw);
    if (!parsed) return null;
    const updatedAt = Number(parsed.updated_at || 0);
    if (!updatedAt || Date.now() - updatedAt > STALE_MS) {
      // Stale — pretend it isn't there so we never silently surface
      // an assumption the user set months ago and probably forgot.
      return null;
    }
    const out = {};
    for (const f of SHARED_FIELDS) {
      if (typeof parsed[f] === 'number' && Number.isFinite(parsed[f])) {
        out[f] = parsed[f];
      }
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

/**
 * Write the shared assumption values for this user. Silently no-ops if
 * storage is unavailable (Safari private mode, full quota, SSR).
 *
 * Only writes fields listed in `SHARED_FIELDS` — page-specific keys in
 * the input object are ignored.
 */
export function writeSharedAssumptions(userId, partial) {
  if (!userId || typeof window === 'undefined' || !partial) return;
  try {
    const existing = _safeParse(window.localStorage.getItem(_key(userId))) || {};
    const next = { ...existing };
    for (const f of SHARED_FIELDS) {
      if (partial[f] != null && Number.isFinite(Number(partial[f]))) {
        next[f] = Number(partial[f]);
      }
    }
    next.updated_at = Date.now();
    window.localStorage.setItem(_key(userId), JSON.stringify(next));
  } catch {
    /* private browsing / quota exceeded — best-effort only */
  }
}

/**
 * Helper for page components: given the assumption block the server
 * returned and the user's id, return an effective default block that
 * fills in any unset shared fields from the shared store.
 *
 * Server-side values always win — we never overwrite an explicit
 * per-page value the user committed on that page.
 */
export function withSharedFallbacks(serverAssumptions, userId, fallbacks = {}) {
  const a = serverAssumptions || {};
  const shared = readSharedAssumptions(userId) || {};
  const out = { ...fallbacks, ...shared, ...a };
  // Re-apply any explicit null values from server (intentional clears).
  for (const k of Object.keys(a)) {
    if (a[k] === null) out[k] = null;
  }
  return out;
}
